import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { DatePipe, NgIf } from "@angular/common";
import { RouterLink } from "@angular/router";

import { DateChipComponent } from "../date-chip/date-chip.component";
import { AudiencePillComponent } from "../audience-pill/audience-pill.component";
import { DEFAULT_ACCENT, EventWithSeries } from "../../models/event.models";

/**
 * The single event card used on the events hub, the series page and search
 * results. One component, so those three views can never drift apart.
 */
@Component({
    selector: "app-event-card",
    standalone: true,
    imports: [NgIf, DatePipe, RouterLink, DateChipComponent, AudiencePillComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./event-card.component.html",
    styleUrl: "./event-card.component.scss",
})
export class EventCardComponent {
    @Input({ required: true }) event!: EventWithSeries;
    /** The occurrence being shown — differs from event.starts_at for recurring events. */
    @Input() start: Date | null = null;
    /** Hides the image and tightens spacing, for dense lists. */
    @Input() compact = false;
    /** Shows which series this belongs to. Off on the series page itself. */
    @Input() showSeries = true;

    get accent(): string {
        return this.event.series?.accent_color || DEFAULT_ACCENT;
    }

    get startDate(): Date | null {
        if (this.start) {
            return this.start;
        }
        return this.event.starts_at ? new Date(this.event.starts_at) : null;
    }

    get endDate(): Date | null {
        return this.event.ends_at ? new Date(this.event.ends_at) : null;
    }

    /** Prefer the human phrasing ("Right after Maghreb") over a clock time. */
    get timeLabel(): string | null {
        if (this.event.time_note) {
            return this.event.time_note;
        }
        if (this.event.is_tba || !this.startDate || this.event.all_day) {
            return null;
        }
        return null; // rendered from the dates in the template
    }

    get showClock(): boolean {
        return (
            !this.event.time_note &&
            !this.event.all_day &&
            !this.event.is_tba &&
            !!this.startDate
        );
    }

    get registersHere(): boolean {
        return this.event.registration_mode === "email";
    }
}
